import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Share2, Check, Lock, PartyPopper, Cpu, BookUser } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/context/AuthContext";

const ROBOT = "/robots/skins/egg.webp";

export const ActivationCard = ({ t, user, setUser, onCreateAvatar }) => {
  const a = t.activation;
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const shares = Math.min(user.referral_shares || 0, 3);
  const justActivated = user.service_active && shares >= 3;

  const inviteLink = `${window.location.origin}/register?ref=${user.referral_code}`;
  const waMessage = `${a.waText} ${inviteLink}`;

  const shareWhatsApp = async () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(waMessage)}`, "_blank", "noopener");
    countShare();
  };

  const countShare = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/referral/share", { channel: "whatsapp" });
      setUser({ ...user, referral_shares: data.shares, service_active: data.service_active });
      if (data.service_active && data.shares === 3) {
        toast.success(a.congratsToast);
      } else if (!data.service_active) {
        toast.info(`${a.progressToast} ${Math.min(data.shares, 3)}/3`);
      }
    } catch {
      toast.error("Error");
    } finally {
      setBusy(false);
    }
  };

  const [contacts, setContacts] = useState([]);
  const contactsSupported = typeof navigator !== "undefined" && "contacts" in navigator && "select" in (navigator.contacts || {});

  const pickContacts = async () => {
    try {
      const picked = await navigator.contacts.select(["name", "tel"], { multiple: true });
      const clean = (picked || [])
        .map((c) => ({ name: c.name?.[0] || "?", tel: (c.tel?.[0] || "").replace(/\D/g, "") }))
        .filter((c) => c.tel.length >= 10);
      if (clean.length) setContacts(clean.slice(0, 10));
      else toast.info(a.noContacts);
    } catch {
      // user cancelled the picker
    }
  };

  const inviteContact = (c) => {
    window.open(`https://wa.me/${c.tel}?text=${encodeURIComponent(waMessage)}`, "_blank", "noopener");
    setContacts((cs) => cs.filter((x) => x.tel !== c.tel));
    countShare();
  };

  if (user.service_active && !justActivated) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative rounded-3xl border p-6 mb-6 overflow-hidden ${
        user.service_active
          ? "border-[#00FFAA]/40 bg-[#00FFAA]/5 shadow-[0_0_30px_rgba(0,255,170,0.15)]"
          : "border-[#FFD700]/35 bg-[#FFD700]/5 shadow-[0_0_25px_rgba(255,215,0,0.1)]"
      }`}
      data-testid="activation-card"
    >
      <div className="absolute -top-16 -right-16 w-44 h-44 blur-[100px] opacity-20 pointer-events-none" style={{ backgroundColor: user.service_active ? "#00FFAA" : "#FFD700" }} />

      <AnimatePresence mode="wait">
        {user.service_active ? (
          <motion.div key="done" initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col sm:flex-row items-center gap-5" data-testid="activation-congrats">
            <motion.span
              animate={{ rotate: [0, -8, 8, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
              className="w-16 h-16 rounded-3xl flex items-center justify-center bg-[#00FFAA]/15 border border-[#00FFAA]/50 text-[#00FFAA] shrink-0"
            >
              <PartyPopper size={30} />
            </motion.span>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="font-heading font-black text-lg text-[#00FFAA]">{a.congratsTitle}</h3>
              <p className="text-xs text-white/60 mt-1">{a.congratsSub}</p>
            </div>
            <button
              data-testid="activation-create-avatar-btn"
              onClick={() => (onCreateAvatar ? onCreateAvatar() : navigate("/dashboard"))}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-[10px] font-bold uppercase tracking-widest text-[#05050A] bg-gradient-to-r from-[#00FFAA] to-[#00F5FF] shadow-[0_0_20px_rgba(0,255,170,0.45)] hover:-translate-y-0.5 transition-all shrink-0"
            >
              <Cpu size={13} /> {a.createBtn}
            </button>
          </motion.div>
        ) : (
          <motion.div key="locked" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex flex-col sm:flex-row items-center gap-5 mb-5">
              <motion.img
                src={ROBOT}
                alt="egg"
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="w-20 h-20 object-contain drop-shadow-[0_0_18px_rgba(255,215,0,0.4)] shrink-0"
              />
              <div className="flex-1 text-center sm:text-left">
                <h3 className="font-heading font-black text-base text-[#FFD700] flex items-center justify-center sm:justify-start gap-2">
                  <Lock size={15} /> {a.title}
                </h3>
                <p className="text-xs text-white/60 mt-1.5 leading-relaxed">{a.desc}</p>
              </div>
            </div>

            {/* Share meter 0/3 */}
            <div className="flex items-center justify-center gap-3 mb-5" data-testid="share-meter">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex flex-col items-center gap-1.5">
                  <span
                    className={`w-11 h-11 rounded-full flex items-center justify-center border-2 transition-all ${
                      i < shares
                        ? "bg-[#00FFAA]/15 border-[#00FFAA] text-[#00FFAA] shadow-[0_0_15px_rgba(0,255,170,0.4)]"
                        : "bg-white/[0.03] border-white/15 text-white/25"
                    }`}
                    data-testid={`share-dot-${i}`}
                  >
                    {i < shares ? <Check size={18} /> : <Share2 size={15} />}
                  </span>
                  <span className={`text-[8px] font-bold uppercase tracking-widest ${i < shares ? "text-[#00FFAA]" : "text-white/25"}`}>
                    {a.friend} {i + 1}
                  </span>
                </div>
              ))}
              <span className="font-heading font-black text-2xl ml-2" style={{ color: shares >= 3 ? "#00FFAA" : "#FFD700" }} data-testid="share-count">
                {shares}/3
              </span>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                data-testid="whatsapp-share-btn"
                onClick={shareWhatsApp}
                disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full text-xs font-bold uppercase tracking-widest text-white bg-[#25D366] shadow-[0_0_22px_rgba(37,211,102,0.45)] hover:shadow-[0_0_35px_rgba(37,211,102,0.7)] hover:-translate-y-0.5 transition-all disabled:opacity-60"
              >
                <Share2 size={15} /> {a.shareBtn}
              </button>
              {contactsSupported && (
                <button
                  data-testid="pick-contacts-btn"
                  onClick={pickContacts}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full text-xs font-bold uppercase tracking-widest text-[#00F5FF] border border-[#00F5FF]/40 bg-[#00F5FF]/8 hover:bg-[#00F5FF]/15 transition-colors"
                >
                  <BookUser size={15} /> {a.pickContactsBtn}
                </button>
              )}
              <button
                data-testid="copy-link-btn"
                onClick={() => { navigator.clipboard?.writeText(inviteLink); toast.success(a.copied); }}
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full text-[10px] font-bold uppercase tracking-widest text-white/60 border border-white/15 bg-white/5 hover:text-white transition-colors"
              >
                {a.copyBtn}
              </button>
            </div>

            {/* Picked phone contacts, one WhatsApp invite per friend */}
            {contacts.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="picked-contacts-list">
                <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/40">{a.pickedTitle}</p>
                {contacts.map((c) => (
                  <div key={c.tel} className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-2.5">
                    <span className="w-8 h-8 rounded-full bg-[#00F5FF]/10 border border-[#00F5FF]/30 text-[#00F5FF] flex items-center justify-center text-xs font-black shrink-0">
                      {c.name.charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-white truncate">{c.name}</p>
                      <p className="text-[10px] text-white/35">{c.tel}</p>
                    </div>
                    <button
                      onClick={() => inviteContact(c)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[9px] font-bold uppercase tracking-widest text-white bg-[#25D366] shadow-[0_0_12px_rgba(37,211,102,0.35)] shrink-0"
                      data-testid={`invite-contact-${c.tel}`}
                    >
                      <Share2 size={11} /> {a.sendBtn}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
